import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CardHeader } from '../../components/CardHeader';
import { Drawer } from '../../components/Drawer';
import { Button } from '../../components/Button';
import { TextField } from '../../components/TextFieldInput';
import { TextArea } from '../../components/TextAreaInput';
import { SelectInput } from '../../components/SelectInput';
import { head } from 'lodash';

const LABEL_SIZE = 80;

function getSchema(intl) {
  return Yup.object().shape({
    name: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    description: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    userId: Yup.number().when('id', {
      is: false,
      then: Yup.number().required(
        intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
      ),
    }),
  });
}

export default function EditProjectsMenu({
  intl,
  project,
  hasPermission,
  onClose,
  open,
  entityId,
  getProjects,
}) {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    async function getUsers() {
      if (!project && open) {
        const { data } = await axios.get(
          `/business-entities/${entityId}/users`
        );
        if (data) setUsers(data);
      }
    }
    getUsers();
  }, [project, entityId, open]);

  const initialValues = {
    name: '',
    description: '',
    userId: head(users)?.id,
  };

  const formik = useFormik({
    initialValues: project || initialValues,
    enableReinitialize: true,
    validationSchema: getSchema(intl),
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true);
      try {
        if (project) await axios.put(`/projects/${project.id}`, values);
        else
          await axios.post(`/projects`, {
            ...values,
            userId: +values.userId,
          });
        await getProjects();
        onClose();
      } finally {
        setSubmitting(false);
      }
    },
  });

  function buildName(user) {
    return `${user.email} (${user.firstName} ${user.lastName})`;
  }

  return (
    <Drawer onClose={onClose} containerId="projects-container" open={open}>
      <div className="h-100" style={{ width: 400 }}>
        <CardHeader
          title={
            project
              ? project.name
              : intl.formatMessage({ id: 'PROJECTS.CREATE.TITLE' })
          }
          subtitle={intl.formatMessage({
            id: project ? 'PROJECTS.EDIT.SUBTITLE' : 'PROJECTS.CREATE.SUBTITLE',
          })}
          iconPath="/media/svg/senseworks/projects.svg"
        />
        <div className="card-body">
          <TextField
            labelClasses="font-weight-bolder"
            field="name"
            inputSize="100%"
            labelMinSize={LABEL_SIZE}
            formik={formik}
            label={intl.formatMessage({ id: 'NAME' })}
          />
          <TextArea
            labelClass="font-weight-bolder"
            field="description"
            inputSize="100%"
            direction="align-items-center"
            labelMinSize={LABEL_SIZE}
            formik={formik}
            label={intl.formatMessage({ id: 'DESCRIPTION' })}
          />
          {!project && (
            <SelectInput
              classes="d-block w-100"
              labelClasses="heavy-text-muted"
              field="userId"
              options={users}
              label={intl.formatMessage({
                id: 'PROJECTS.CREATE.SELECT_MANAGER',
              })}
              formik={formik}
              description="name"
              buildDescription={buildName}
            />
          )}

          <div className="d-flex mt-3">
            {(project
              ? hasPermission('projects*', 'U')
              : hasPermission('projects*', 'C')) && (
              <Button
                classes="w-75px px-12 py-2 my-1 d-flex align-items-center justify-content-center btn btn-primary"
                type="submit"
                formik={formik}
                onClick={formik.handleSubmit}
                title={intl.formatMessage({ id: project ? 'SAVE' : 'CREATE' })}
              />
            )}
            <Button
              classes="w-75px cancel-bg ml-3 cancel-btn cancel-text-color px-4"
              type="button"
              onClick={onClose}
              title={intl.formatMessage({ id: 'CANCEL' })}
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
}
